import { Button, Helper, Icon } from 'Components';
import { CUSTOMIZETABLE } from 'constants/apis';
import Fetch from 'helpers/fetch';
import { buildUrl } from 'helpers/misc';
import i18n from 'i18next';
import log from 'loglevel';
import PropTypes from 'prop-types';
import { viewCodeSpecialCases } from 'helpers/column-save';
import React from 'react';
import AppContext from 'src/AppContext';
import './EditColumns.css';
import EditColumnsListItem from './EditColumnsListItem/EditColumnsListItem';

class EditColumns extends React.Component {
  constructor(props) {
    super(props);

    const { keys, keyPairs, requiredFields } = this.props.table;

    this.requiredKeys = (requiredFields || []).map((field) => field.toLowerCase()) || [];

    const selected = keys.map((key) => ({
      id: key,
      selected: true,
      title: keyPairs[key],
    }));

    const available = Object.keys(keyPairs)
      .filter((x) => !keys.includes(x) && !this.requiredKeys.includes(x.toLocaleLowerCase()))
      .map((key) => ({
        id: key,
        selected: false,
        title: keyPairs[key],
      }));

    this.state = {
      isOverviewTable: this.props.table.viewCode.includes('overview'),
      selected,
      available,
      indexFrom: undefined,
      indexTo: undefined,
      listFrom: undefined,
      listTo: undefined,
    };

    this.handleToggle = this.handleToggle.bind(this);
    this.handleDragOverSelected = this.handleDragOverSelected.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragOverAvailable = this.handleDragOverAvailable.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleDefault = this.handleDefault.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
  }

  getReorganizedLists() {
    const { indexFrom, indexTo, listFrom, listTo } = this.state;
    let { selected, available } = this.state;

    if (listFrom === 'selected' && listTo === 'selected') {
      if (indexFrom < indexTo) {
        selected = [
          ...selected.slice(0, indexFrom),
          ...selected.slice(indexFrom + 1, indexTo + 1),
          selected[indexFrom],
          ...selected.slice(indexTo + 1),
        ];
      } else if (indexTo < indexFrom) {
        selected = [
          ...selected.slice(0, indexTo),
          selected[indexFrom],
          ...selected.slice(indexTo, indexFrom),
          ...selected.slice(indexFrom + 1),
        ];
      }
    } else if (listFrom === 'available' && listTo === 'available') {
      if (indexFrom < indexTo) {
        available = [
          ...available.slice(0, indexFrom),
          ...available.slice(indexFrom + 1, indexTo + 1),
          available[indexFrom],
          ...available.slice(indexTo + 1),
        ];
      } else if (indexTo < indexFrom) {
        available = [
          ...available.slice(0, indexTo),
          available[indexFrom],
          ...available.slice(indexTo, indexFrom),
          ...available.slice(indexFrom + 1),
        ];
      }
    } else if (listFrom === 'available' && listTo === 'selected') {
      selected = [...selected.slice(0, indexTo), available[indexFrom], ...selected.slice(indexTo)];
      available = [...available.slice(0, indexFrom), ...available.slice(indexFrom + 1)];
    } else if (listFrom === 'selected' && listTo === 'available') {
      if (
        selected[indexFrom] != null &&
        selected[indexFrom].id != null &&
        this.requiredKeys.includes(selected[indexFrom].id.toLowerCase())
      ) {
        return { selected, available };
      }
      available = [...available.slice(0, indexTo), selected[indexFrom], ...available.slice(indexTo)];
      selected = [...selected.slice(0, indexFrom), ...selected.slice(indexFrom + 1)];
    }

    return { selected, available };
  }

  handleToggle(id, isSelected) {
    if (this.requiredKeys.includes(id.toLowerCase())) {
      return;
    }
    const TO = isSelected ? 'available' : 'selected';
    const FROM = isSelected ? 'selected' : 'available';
    const i = this.state[FROM].findIndex((x) => x.id === id);

    this.setState((prevState) => ({
      [TO]: [...prevState[TO], prevState[FROM][i]],
      [FROM]: [...prevState[FROM].slice(0, i), ...prevState[FROM].slice(i + 1)],
    }));
  }

  handleSave() {
    const keys = this.state.selected.map((item) => item.id);
    const DataList = this.state.selected.map((item) => ({ DataField: item.id }));

    const parameters = {
      viewCode: this.props.table.viewCode,
      applicationCode: 'CP',
    };

    Fetch.Post(buildUrl(CUSTOMIZETABLE, parameters), { DataList }).catch((err) => log.error(err));

    const shouldLoad = !this.props.table.handleDataManually;

    const isSpecialCase = Object.keys(viewCodeSpecialCases).includes(this.props.settingTableCode);

    this.props.setAppState(
      {
        settingTableCode: null,
        [this.props.settingTableCode]: {
          ...this.props.table,
          keys,
          shouldLoad,
        },
      },
      isSpecialCase
        ? () =>
            this.props.setAppState((prevState) => ({
              [viewCodeSpecialCases[this.props.settingTableCode]]: {
                ...prevState[viewCodeSpecialCases[this.props.settingTableCode]],
                shouldLoad: true,
              },
            }))
        : null,
    );
  }

  handleDefault() {
    const { keyPairs, defaultKeys } = this.props.table;

    const selected = defaultKeys.map((key) => ({
      id: key,
      selected: true,
      title: keyPairs[key],
    }));

    const available = Object.keys(keyPairs)
      .filter((x) => !defaultKeys.includes(x) && !this.requiredKeys.includes(x.toLowerCase()))
      .map((key) => ({
        id: key,
        selected: false,
        title: keyPairs[key],
      }));

    this.setState({
      selected,
      available,
    });
  }

  handleDragStart(e, i, listFrom) {
    this.dragged = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    this.setState({
      indexFrom: i,
      indexTo: i,
      listFrom,
    });
  }

  handleDragOverSelected(e) {
    e.preventDefault();
    const indexTo = Number(e.target.dataset.id);
    if (indexTo !== this.state.indexTo) {
      this.setState({
        listTo: 'selected',
        indexTo,
      });
    }
  }

  handleDragOverAvailable(e) {
    e.preventDefault();
    const indexTo = Number(e.target.dataset.id);
    if (indexTo !== this.state.indexTo) {
      this.setState({
        listTo: 'available',
        indexTo,
      });
    }
  }

  handleDrop() {
    const { selected, available } = this.getReorganizedLists();

    this.setState({
      listFrom: undefined,
      listTo: undefined,
      indexFrom: undefined,
      indexTo: undefined,
      selected,
      available,
    });
  }

  render() {
    const disabled = this.state.isOverviewTable && this.state.selected.length > 3;
    const { selected, available } = this.getReorganizedLists();

    return (
      <div className="edit-columns">
        <div className="edit-columns__close">
          <Button onClick={() => this.props.setAppState({ settingTableCode: null })}>
            {i18n.t('editColumns:Close')} <Icon icon="close" />
          </Button>
        </div>
        <div className="edit-columns__title">
          <Helper text={i18n.t('tutorials:editColumns.settingColumnHeaders')}>
            <h2>{i18n.t('editColumns:Columns')}</h2>
          </Helper>
        </div>
        <div className="edit-columns__buttons">
          <Button className="button--primary" onClick={this.handleDefault}>
            {i18n.t('editColumns:Default')}
          </Button>
          <Button className="button--primary" onClick={this.handleSave}>
            {i18n.t('editColumns:Save')}
          </Button>
        </div>
        <div className="edit-columns__tables">
          <div className="edit-columns__tables__title">
            <h4>{i18n.t('editColumns:Selected')}</h4>
          </div>
          <div onDragOver={this.handleDragOverSelected} onDrop={this.handleDrop}>
            <ul>
              {selected.map((item, i) => (
                <EditColumnsListItem
                  key={item.id}
                  i={i}
                  id={item.id}
                  onDragStart={(e) => this.handleDragStart(e, i, 'selected')}
                  handleToggle={this.handleToggle}
                  title={item.title}
                  isActive={this.state.indexTo === i && this.state.listTo === 'selected'}
                  checked
                />
              ))}
            </ul>
          </div>
          <div className="edit-columns__tables__title">
            <h4>{i18n.t('editColumns:Available')}</h4>
          </div>
          <div
            onDragOver={this.handleDragOverAvailable}
            onDrop={this.handleDrop}
            className={disabled ? 'edit-columns__tables--disable' : ''}
          >
            <ul>
              {available.map((item, i) => (
                <EditColumnsListItem
                  key={item.id}
                  i={i}
                  id={item.id}
                  onDragStart={(e) => this.handleDragStart(e, i, 'available')}
                  handleToggle={this.handleToggle}
                  title={item.title}
                  isActive={this.state.indexTo === i && this.state.listTo === 'available'}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

EditColumns.propTypes = {
  setAppState: PropTypes.func,
  settingTableCode: PropTypes.string,
  table: PropTypes.shape({
    viewCode: PropTypes.string,
    keys: PropTypes.arrayOf(PropTypes.string),
    defaultKeys: PropTypes.arrayOf(PropTypes.string),
    keyPairs: PropTypes.shape({}),
    title: PropTypes.string,
    handleDataManually: PropTypes.bool,
  }),
};

export default (props) => (
  <AppContext.Consumer>{(context) => <EditColumns {...props} setAppState={context.setAppState} />}</AppContext.Consumer>
);



// WEBPACK FOOTER //
// ./src/components/EditColumns/EditColumns.jsx